import React, { useRef, useState, useCallback, useEffect } from "react";
import {
    DmsPage,
    DmsButtonGroup,
    DmsTable,
    DmsAltBox,
    DmsTabber,
    DmsButton,
    DmsPermissions,
    DmsModal,
    DmsTextField,
    fetchData,
    fetchUrl,
    HTTP_STATUS,
    intl
} from "dmsbasereact";
import { Typography, Box, Chip, IconButton, Tooltip, Grid, Alert } from "@mui/material";
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import VisibilityIcon from '@mui/icons-material/Visibility';
import Swal from "sweetalert2";
import { DmsFileViewerModal } from "../../components/DmsFileViewer";

import { Permissions } from "../../constants/Permissions";
import { ReturnStatusEnum, ReturnStatus } from "../../constants/ReturnStatus";
import { useReturnReasons } from '../../constants/ReturnReasons';
import { DmsReturnEditButton } from "../../components/DmsReturnEditButton/DmsReturnEditButton";
import { DmsReturnViewButton } from "../../components/DmsReturnViewButton/DmsReturnViewButton";
import { DocumentUploadSlot } from "../../components/DocumentUploadSlot/DocumentUploadSlot";
import { formatCnpj } from "../../utils/formatters";

export const ReturnManagementPage = () => {
    const ReturnReasons = useReturnReasons();
    const pendingTable = useRef(null);
    const completedTable = useRef(null);
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [selectedReturn, setSelectedReturn] = useState(null);
    const [sapTrackingCode, setSapTrackingCode] = useState("");
    const [invoiceNumber, setInvoiceNumber] = useState("");
    const [saving, setSaving] = useState(false);
    const [files, setFiles] = useState({ evidencia: null, laudo: null });
    const [existingFiles, setExistingFiles] = useState([]);
    const [, setLoadingFiles] = useState(false);
    const [viewerOpen, setViewerOpen] = useState(false);
    const [selectedFile, setSelectedFile] = useState(null);
    const [reasonOptions, setReasonOptions] = useState([]);

    // Fetch return reason options from backend for filters (with translations)
    useEffect(() => {
        const fetchReasonOptions = async () => {
            try {
                const response = await fetchData('/return/management/reason-options', 'GET', true);
                if (response.status === HTTP_STATUS.OK_200 && response.data?.data) {
                    // Map backend options to translated labels
                    const translatedOptions = response.data.data.map(opt => ({
                        value: opt.value,
                        label: ReturnReasons[opt.value] || opt.label
                    }));
                    setReasonOptions(translatedOptions);
                }
            } catch (error) {
                console.error("Failed to load reason options:", error);
            }
        };
        fetchReasonOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const loadExistingFiles = useCallback(async (returnPk) => {
        setLoadingFiles(true);
        try {
            const response = await fetchData(`/return/${returnPk}/complete`, 'GET', true);
            if (response.status === HTTP_STATUS.OK_200 && response.data?.data?.files) {
                setExistingFiles(response.data.data.files);
            }
        } catch (error) {
            console.error("Failed to load existing files:", error);
        } finally {
            setLoadingFiles(false);
        }
    }, []);

    const handleOpenEdit = useCallback((row) => {
        setSelectedReturn(row);
        setSapTrackingCode(row.sap_tracking_code || "");
        setInvoiceNumber(row.invoice_number || "");
        setFiles({ evidencia: null, laudo: null });
        setExistingFiles([]);
        loadExistingFiles(row.pk);
        setEditModalOpen(true);
    }, [loadExistingFiles]);

    const handleCloseEdit = useCallback(() => {
        setEditModalOpen(false);
        setSelectedReturn(null);
        setSapTrackingCode("");
        setInvoiceNumber("");
        setFiles({ evidencia: null, laudo: null });
        setExistingFiles([]);
    }, []);

    const handleFileSelect = useCallback((docName, file) => {
        setFiles(prev => ({ ...prev, [docName]: file }));
    }, []);

    const handleViewFile = useCallback((file) => {
        setSelectedFile({
            filePk: file.pk,
            fileName: file.original_filename
        });
        setViewerOpen(true);
    }, []);

    const handleCloseViewer = useCallback(() => {
        setViewerOpen(false);
        setSelectedFile(null);
    }, []);

    const hasEvidencia = useCallback(() => {
        return files.evidencia || existingFiles.some(f => f.document_name === 'evidencia');
    }, [files, existingFiles]);

    const hasLaudo = useCallback(() => {
        return files.laudo || existingFiles.some(f => f.document_name === 'laudo');
    }, [files, existingFiles]);

    const getCurrentStatus = useCallback(() => {
        if (!selectedReturn) return null;
        return typeof selectedReturn.status === 'object'
            ? selectedReturn.status.value
            : selectedReturn.status;
    }, [selectedReturn]);

    const isPending = getCurrentStatus() === ReturnStatusEnum.PENDING;
    const isCollected = getCurrentStatus() === ReturnStatusEnum.COLLECTED;

    const uploadFiles = useCallback(async () => {
        const uploadedFiles = [];
        for (const [docName, file] of Object.entries(files)) {
            if (file) {
                const response = await fetchUrl(
                    '/return/file_upload',
                    'POST',
                    true,
                    file,
                    false,
                    true,
                    null,
                    null,
                    'multipart/form-data'
                );
                if (response.status === HTTP_STATUS.CREATED_201 && response.data?.data?.pk) {
                    uploadedFiles.push({
                        file_pk: response.data.data.pk,
                        document_name: docName
                    });
                }
            }
        }
        return uploadedFiles;
    }, [files]);

    const handleRegisterCollection = useCallback(async () => {
        if (!selectedReturn) return;

        if (!hasEvidencia()) {
            Swal.fire({
                icon: "warning",
                title: intl.get("RequiredDocument").d("Required Document"),
                text: intl.get("CollectionEvidenceRequired").d("Collection evidence document is required."),
            });
            return;
        }

        const result = await Swal.fire({
            icon: "question",
            title: intl.get("ConfirmCollection").d("Confirm Collection"),
            text: intl.get("ConfirmRegisterCollection").d("Are you sure you want to register the collection?"),
            showCancelButton: true,
            confirmButtonText: intl.get("Yes").d("Yes"),
            cancelButtonText: intl.get("No").d("No"),
        });

        if (!result.isConfirmed) return;

        setSaving(true);
        try {
            const uploadedFiles = await uploadFiles();

            const requestBody = {
                files: uploadedFiles,
                new_status: ReturnStatusEnum.COLLECTED,
                sap_tracking_code: sapTrackingCode || null,
                invoice_number: invoiceNumber || null
            };

            const response = await fetchData(
                `/return/${selectedReturn.pk}/management`,
                "PUT",
                true,
                requestBody
            );

            if (response.status === HTTP_STATUS.OK_200) {
                await Swal.fire({
                    icon: "success",
                    title: intl.get("Success").d("Success"),
                    text: intl.get("CollectionRegistered").d("Collection registered successfully"),
                });
                handleCloseEdit();
                if (pendingTable.current) {
                    pendingTable.current.updateTable();
                }
            }
        } catch (error) {
            console.error("Failed to register collection:", error);
            Swal.fire({
                icon: "error",
                title: intl.get("Error").d("Error"),
                text: error?.response?.data?.detail || intl.get("FailedToRegisterCollection").d("Failed to register collection"),
            });
        } finally {
            setSaving(false);
        }
    }, [selectedReturn, hasEvidencia, handleCloseEdit, uploadFiles]);

    const handleCompleteProcess = useCallback(async () => {
        if (!selectedReturn) return;

        if (!hasEvidencia() || !hasLaudo()) {
            Swal.fire({
                icon: "warning",
                title: intl.get("RequiredDocuments").d("Required Documents"),
                text: intl.get("BothDocumentsRequired").d("Both evidence and destruction report documents are required."),
            });
            return;
        }

        const result = await Swal.fire({
            icon: "question",
            title: intl.get("ConfirmCompletion").d("Confirm Completion"),
            text: intl.get("ConfirmCloseProcess").d("Are you sure you want to close this return process?"),
            showCancelButton: true,
            confirmButtonText: intl.get("Yes").d("Yes"),
            cancelButtonText: intl.get("No").d("No"),
        });

        if (!result.isConfirmed) return;

        setSaving(true);
        try {
            const uploadedFiles = await uploadFiles();

            const requestBody = {
                files: uploadedFiles,
                new_status: ReturnStatusEnum.COMPLETED,
                sap_tracking_code: sapTrackingCode || null,
                invoice_number: invoiceNumber || null
            };

            const response = await fetchData(
                `/return/${selectedReturn.pk}/management`,
                "PUT",
                true,
                requestBody
            );

            if (response.status === HTTP_STATUS.OK_200) {
                await Swal.fire({
                    icon: "success",
                    title: intl.get("Success").d("Success"),
                    text: intl.get("ProcessCompleted").d("Return process completed successfully"),
                });
                handleCloseEdit();
                if (pendingTable.current) {
                    pendingTable.current.updateTable();
                }
            }
        } catch (error) {
            console.error("Failed to complete process:", error);
            Swal.fire({
                icon: "error",
                title: intl.get("Error").d("Error"),
                text: error?.response?.data?.detail || intl.get("FailedToCompleteProcess").d("Failed to complete process"),
            });
        } finally {
            setSaving(false);
        }
    }, [selectedReturn, hasEvidencia, hasLaudo, handleCloseEdit, uploadFiles]);

    // Prepare status options for select filter (PENDING and COLLECTED)
    // Use integer enum values for filtering, not text labels
    const statusOptions = [
        { value: ReturnStatusEnum.PENDING, label: ReturnStatus.getText(ReturnStatusEnum.PENDING) },
        { value: ReturnStatusEnum.COLLECTED, label: ReturnStatus.getText(ReturnStatusEnum.COLLECTED) }
    ];

    const pendingHeader = [
        {
            label: intl.get("ID").d("ID"),
            column_name: "pk",
            filter: true,
            width: "80px"
        },
        {
            label: intl.get("ReturnReason").d("Return Reason"),
            column_name: "return_reason_name",
            filter: true,
            control: "select",
            options: reasonOptions,
            element: (row) => {
                const reasonName = row?.return_reason?.name || row?.return_reason_name;
                if (reasonName && ReturnReasons[reasonName]) {
                    return ReturnReasons[reasonName];
                }
                return reasonName || '-';
            }
        },
        {
            label: intl.get("box_count").d("Box Count"),
            column_name: "quantity",
            filter: false,
            width: "100px"
        },
        {
            label: intl.get("Organization").d("Organization"),
            column_name: "organization_name",
            filter: true
        },
        {
            label: intl.get("CNPJ").d("CNPJ"),
            column_name: "organization_cnpj",
            filter: true,
            element: (row) => formatCnpj(row?.organization_cnpj) || '-'
        },
        {
            label: intl.get("Status").d("Status"),
            column_name: "status",
            filter: true,
            control: "select",
            options: statusOptions,
            element: (row) => {
                const statusValue = typeof row.status === 'object' ? row.status.value : row.status;
                return (
                    <Chip
                        label={ReturnStatus.getText(statusValue)}
                        color={ReturnStatus.getColor(statusValue)}
                        size="small"
                    />
                );
            }
        },
        {
            label: intl.get("SAPCRMCode").d("SAP CRM Code"),
            column_name: "sap_tracking_code",
            filter: true,
            element: (row) => row?.sap_tracking_code || '-'
        },
        {
            label: intl.get("LastUpdated").d("Last Updated"),
            column_name: "updated_on",
            filter: false,
            element: (row) => row?.updated_on ? new Date(row.updated_on).toLocaleDateString('pt-BR') : '-'
        },
        {
            label: intl.get("Actions").d("Actions"),
            column_name: "actions",
            width: "120px",
            sortable: false,
            element: (row) => (
                <DmsButtonGroup>
                    <DmsReturnViewButton row={row} />
                    <DmsPermissions permissions={[Permissions.RETURN_MANAGEMENT_UPDATE]}>
                        <DmsReturnEditButton table={pendingTable} row={row} managementMode />
                        <Tooltip title={intl.get("ManageCollectionDestruction").d("Manage Collection and Destruction")}>
                            <IconButton
                                size="small"
                                onClick={() => handleOpenEdit(row)}
                                color="primary"
                            >
                                <AssignmentTurnedInIcon />
                            </IconButton>
                        </Tooltip>
                    </DmsPermissions>
                </DmsButtonGroup>
            )
        },
    ];

    // Header for completed returns (view only)
    const completedHeader = [
        {
            label: intl.get("ID").d("ID"),
            column_name: "pk",
            filter: true,
            width: "80px"
        },
        {
            label: intl.get("ReturnReason").d("Return Reason"),
            column_name: "return_reason_name",
            filter: true,
            control: "select",
            options: reasonOptions,
            element: (row) => {
                const reasonName = row?.return_reason?.name || row?.return_reason_name;
                if (reasonName && ReturnReasons[reasonName]) {
                    return ReturnReasons[reasonName];
                }
                return reasonName || '-';
            }
        },
        {
            label: intl.get("box_count").d("Box Count"),
            column_name: "quantity",
            filter: false,
            width: "100px"
        },
        {
            label: intl.get("Organization").d("Organization"),
            column_name: "organization_name",
            filter: true
        },
        {
            label: intl.get("CNPJ").d("CNPJ"),
            column_name: "organization_cnpj",
            filter: true,
            element: (row) => formatCnpj(row?.organization_cnpj) || '-'
        },
        {
            label: intl.get("Status").d("Status"),
            column_name: "status_label",
            filter: false,
            element: (row) => {
                const statusValue = typeof row.status === 'object' ? row.status.value : row.status;
                return (
                    <Chip
                        label={ReturnStatus.getText(statusValue)}
                        color={ReturnStatus.getColor(statusValue)}
                        size="small"
                    />
                );
            }
        },
        {
            label: intl.get("SAPCRMCode").d("SAP CRM Code"),
            column_name: "sap_tracking_code",
            filter: true,
            element: (row) => row?.sap_tracking_code || '-'
        },
        {
            label: intl.get("LastUpdated").d("Last Updated"),
            column_name: "updated_on",
            filter: false,
            element: (row) => row?.updated_on ? new Date(row.updated_on).toLocaleDateString('pt-BR') : '-'
        },
        {
            label: intl.get("Actions").d("Actions"),
            column_name: "actions",
            width: "80px",
            sortable: false,
            element: (row) => (
                <DmsButtonGroup>
                    <DmsReturnViewButton row={row} />
                    <DmsPermissions permissions={[Permissions.RETURN_MANAGEMENT_UPDATE]}>
                        <DmsReturnEditButton table={pendingTable} row={row} managementMode />
                    </DmsPermissions>
                </DmsButtonGroup>
            )
        },
    ];

    const tabs = [
        {
            title: intl.get("PendingReturns").d("Pending"),
            component: (
                <>
                    <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                            {intl.get("ReturnManagementDescription").d("Manage returns, including collection and destruction process. Add evidence documents and update status.")}
                        </Typography>
                    </Box>
                    <DmsTable
                        header={pendingHeader}
                        managed={true}
                        needs_authentication={true}
                        export_table={false}
                        ref={pendingTable}
                        url={'/return/management/table'}
                    />
                </>
            )
        },
        {
            title: intl.get("CompletedReturns").d("Completed"),
            component: (
                <>
                    <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                            {intl.get("CompletedReturnsDescription").d("View completed returns and their attached documents.")}
                        </Typography>
                    </Box>
                    <DmsTable
                        header={completedHeader}
                        managed={true}
                        needs_authentication={true}
                        export_table={false}
                        ref={completedTable}
                        url={'/return/management/completed/table'}
                    />
                </>
            )
        }
    ];

    return (
        <DmsPage name="ReturnManagement">
            <DmsAltBox.Card>
                <DmsAltBox.Header>
                    <Typography variant='h3' sx={{ fontSize: '1.1rem' }}>
                        {intl.get("ReturnManagement").d("Return Management")}
                    </Typography>
                </DmsAltBox.Header>
                <DmsAltBox.Body>
                    <DmsTabber tabs={tabs} />
                </DmsAltBox.Body>
            </DmsAltBox.Card>

            <DmsModal
                isVisible={editModalOpen}
                onClose={handleCloseEdit}
            >
                <DmsModal.Header
                    title={intl.get("ManageReturn").d("Manage Return")}
                    onClose={handleCloseEdit}
                />
                <DmsModal.Body>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 500 }}>
                        {/* Return Info */}
                        <Box sx={{ mb: 2 }}>
                            <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                {intl.get("ReturnDetails").d("Return Details")}
                            </Typography>
                            <Grid container spacing={2}>
                                <Grid item xs={6}>
                                    <Typography variant="body2" color="textSecondary">
                                        {intl.get("ID").d("ID")}: <strong>{selectedReturn?.pk}</strong>
                                    </Typography>
                                </Grid>
                                <Grid item xs={6}>
                                    <Typography variant="body2" color="textSecondary">
                                        {intl.get("box_count").d("Box Count")}: <strong>{selectedReturn?.quantity}</strong>
                                    </Typography>
                                </Grid>
                                <Grid item xs={6}>
                                    <Typography variant="body2" color="textSecondary">
                                        {intl.get("Batch").d("Batch")}: <strong>{selectedReturn?.batch_number || '-'}</strong>
                                    </Typography>
                                </Grid>
                                <Grid item xs={12}>
                                    <Typography variant="body2" color="textSecondary">
                                        {intl.get("Status").d("Status")}: {' '}
                                        <Chip
                                            label={ReturnStatus.getText(getCurrentStatus())}
                                            color={ReturnStatus.getColor(getCurrentStatus())}
                                            size="small"
                                        />
                                    </Typography>
                                </Grid>
                            </Grid>
                        </Box>

                        {/* Editable Fields */}
                        <Box sx={{ mb: 2 }}>
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6}>
                                    <DmsTextField
                                        label={intl.get("InvoiceNumber").d("Invoice Number")}
                                        value={invoiceNumber}
                                        onChange={(e) => setInvoiceNumber(e.target.value)}
                                        fullWidth
                                        placeholder={intl.get("EnterInvoiceNumber").d("Enter invoice number")}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <DmsTextField
                                        label={intl.get("SAPCRMCode").d("SAP CRM Code")}
                                        value={sapTrackingCode}
                                        onChange={(e) => setSapTrackingCode(e.target.value)}
                                        fullWidth
                                        placeholder={intl.get("EnterSAPCRMCode").d("Enter SAP CRM code")}
                                    />
                                </Grid>
                            </Grid>
                        </Box>

                        {/* Collection Evidence Section */}
                        <Box sx={{ mt: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <Typography variant="subtitle2">
                                    {intl.get("CollectionEvidence").d("Collection Evidence")}
                                    {isPending && (
                                        <Typography component="span" color="error" sx={{ ml: 0.5 }}>*</Typography>
                                    )}
                                </Typography>
                                {existingFiles.find(f => f.document_name === 'evidencia') && (
                                    <Tooltip title={intl.get("ViewDocument").d("View Document")}>
                                        <IconButton
                                            size="small"
                                            onClick={() => handleViewFile(existingFiles.find(f => f.document_name === 'evidencia'))}
                                            sx={{ ml: 1 }}
                                        >
                                            <VisibilityIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                )}
                            </Box>
                            {isPending && !hasEvidencia() && (
                                <Alert severity="info" sx={{ mb: 2 }}>
                                    {intl.get("UploadCollectionEvidence").d("Upload the collection evidence to register the collection.")}
                                </Alert>
                            )}
                            <DocumentUploadSlot
                                docName="evidencia"
                                file={files.evidencia}
                                onFileSelect={handleFileSelect}
                                existingFile={existingFiles.find(f => f.document_name === 'evidencia')}
                                error={isPending && !files.evidencia && !existingFiles.some(f => f.document_name === 'evidencia')}
                            />
                            <Typography variant="caption" color="textSecondary" sx={{ mt: 0.5, display: 'block' }}>
                                {intl.get("EvidenciaDescription").d("Evidence of collection for destruction")}
                            </Typography>
                        </Box>

                        {/* Destruction Report Section */}
                        <Box sx={{ mt: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <Typography variant="subtitle2">
                                    {intl.get("DestructionReport").d("Destruction Report")}
                                    {isCollected && (
                                        <Typography component="span" color="error" sx={{ ml: 0.5 }}>*</Typography>
                                    )}
                                </Typography>
                                {existingFiles.find(f => f.document_name === 'laudo') && (
                                    <Tooltip title={intl.get("ViewDocument").d("View Document")}>
                                        <IconButton
                                            size="small"
                                            onClick={() => handleViewFile(existingFiles.find(f => f.document_name === 'laudo'))}
                                            sx={{ ml: 1 }}
                                        >
                                            <VisibilityIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                )}
                            </Box>
                            {isCollected && !hasLaudo() && (
                                <Alert severity="info" sx={{ mb: 2 }}>
                                    {intl.get("UploadDestructionReport").d("Upload the destruction report to complete the process.")}
                                </Alert>
                            )}
                            <DocumentUploadSlot
                                docName="laudo"
                                file={files.laudo}
                                onFileSelect={handleFileSelect}
                                existingFile={existingFiles.find(f => f.document_name === 'laudo')}
                                error={isCollected && !files.laudo && !existingFiles.some(f => f.document_name === 'laudo')}
                                disabled={isPending && !hasEvidencia()}
                            />
                            <Typography variant="caption" color="textSecondary" sx={{ mt: 0.5, display: 'block' }}>
                                {intl.get("LaudoDescription").d("Final destruction report")}
                            </Typography>
                        </Box>
                    </Box>
                </DmsModal.Body>
                <DmsModal.Footer>
                    <DmsButton
                        variant="secondary"
                        text={intl.get("Cancel").d("Cancel")}
                        onClick={handleCloseEdit}
                        disabled={saving}
                    />
                    {isPending && (
                        <DmsButton
                            variant="primary"
                            text={intl.get("RegisterCollection").d("Register Collection")}
                            onClick={handleRegisterCollection}
                            disabled={saving || !hasEvidencia()}
                        />
                    )}
                    {isCollected && (
                        <DmsButton
                            variant="primary"
                            text={intl.get("CloseProcess").d("Close Process")}
                            onClick={handleCompleteProcess}
                            disabled={saving || !hasEvidencia() || !hasLaudo()}
                        />
                    )}
                </DmsModal.Footer>
            </DmsModal>

            <DmsFileViewerModal
                isOpen={viewerOpen}
                onClose={handleCloseViewer}
                filePk={selectedFile?.filePk}
                fileName={selectedFile?.fileName}
            />
        </DmsPage>
    );
};

export default ReturnManagementPage;
